/* eslint-disable ember/no-runloop */
import { action } from '@ember/object';
import { begin, end, next } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import type { ComparisonSet } from 'frontend-utils/utils/constants/benchmark-insight';
import type * as Highcharts from 'highcharts';
import SkeletonBreakdownChartIllustration from 'lumen/assets/images/benchmark-insight/skeleton/skeleton-breakdown-chart.svg?raw';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import HotelBenchmarkInsightButtonGroupChartKpis from 'lumen/components/hotel/benchmark-insight/button-group-chart-kpis';
import HotelBenchmarkInsightDropdownsDropdownLosDropdownLosSort from 'lumen/components/hotel/benchmark-insight/dropdowns/dropdown-los/dropdown-los-sort';
import type { OccupancyView } from 'lumen/components/hotel/benchmark-insight/dropdowns/dropdown-occupancy-view';
import HotelBenchmarkInsightDropdownsDropdownOccupancyView from 'lumen/components/hotel/benchmark-insight/dropdowns/dropdown-occupancy-view';
import HotelBenchmarkInsightSkeleton from 'lumen/components/hotel/benchmark-insight/skeleton';
import type { BeniTooltipData } from 'lumen/components/hotel/benchmark-insight/tooltips/beni-tooltip';
import HotelBenchmarkInsightTooltipsBeniTooltip from 'lumen/components/hotel/benchmark-insight/tooltips/beni-tooltip';
import type { BeniBreakdown, BeniMetaComparisonSet } from 'lumen/models';
import getElement from 'lumen/modifiers/get-element';
import {
  type BreakdownsDict,
  buildBreakdownDict,
} from 'lumen/utils/benchmark-insight/chart';
import {
  BENI_COMPARISON_SET_LONG_TRANSLATIONS,
  BREAKDOWN_CHART_COLUMN_SERIES_GROUP_PADDING,
  BREAKDOWN_CHART_COLUMN_SERIES_POINT_PADDING,
  BREAKDOWN_CHART_HEIGHT,
  BREAKDOWNS,
  type Kpi,
  KPI_LONG_TRANSLATIONS,
  KPIS,
  type ReferencePeriod,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import type { DateRangeType } from 'lumen/utils/constants/benchmark-insight/beni-table-constant-values';
import {
  DROPDOWN_LOS_SORT_OPTIONS,
  type DropdownLosSortOption,
} from 'lumen/utils/constants/benchmark-insight/los/dropdown-los';
import type {
  LosChartSeries,
  LosChartSeriesData,
} from 'lumen/utils/constants/benchmark-insight/los/los-chart';
import {
  SORT_ORDER,
  type SortOrderType,
} from 'lumen/utils/constants/general-constants';
import type { ChartCustomPoint } from 'lumen/utils/constants/highcharts-constants';
import calculateRemainderFromSum from 'lumen/utils/numeric/get-remainder';

import styles from './los-chart.module.css';

interface HotelBenchmarkInsightChartsLosChartSignature {
  Args: {
    changeSelectedKpiLosChart: (kpi: Kpi) => void;
    changeSelectedLosSortOption: (option: DropdownLosSortOption) => void;
    changeSelectedOccupancyView: (view: OccupancyView) => void;
    isExpanded: boolean;
    isFiltering: boolean;
    los: string | null;
    onUpdateChartReference: (chart: Highcharts.Chart) => void;
    ownComparisonSetBreakdowns: BeniBreakdown[];
    ownComparisonSetMeta: BeniMetaComparisonSet | null;
    pickupTranslation: string;
    primaryComparisonSet: ComparisonSet | null;
    primaryComparisonSetBreakdowns: BeniBreakdown[];
    primaryComparisonSetMeta: BeniMetaComparisonSet | null;
    referencePeriod: ReferencePeriod;
    secondaryComparisonSetBreakdowns: BeniBreakdown[];
    secondaryComparisonSetMeta: BeniMetaComparisonSet | null;
    selectedKpiLosChart: Kpi;
    selectedLosSortOption: DropdownLosSortOption;
    selectedOccupancyView: OccupancyView;
    selectedRange: DateRangeType;
    setLos: (los: string | null) => void;
    showMix: boolean;
    showSkeleton?: boolean;
    showSkeletonAnimation?: boolean;
    varianceTranslation: string;
    visibleKpis: Kpi[];
  };
}

export default class HotelBenchmarkInsightChartsLosChart extends Component<HotelBenchmarkInsightChartsLosChartSignature> {
  @service('benchmark-insight/beni-chart-los-service')
  declare benchmarkInsightBeniChartLosService: Services['benchmark-insight/beni-chart-los-service'];
  // eslint-disable-next-line ember/no-unused-services
  @service('benchmark-insight/beni-chart-service')
  declare benchmarkInsightBeniChartService: Services['benchmark-insight/beni-chart-service'];
  @service('benchmark-insight/beni-service')
  declare benchmarkInsightBeniService: Services['benchmark-insight/beni-service'];
  @service declare intl: Services['intl'];
  // eslint-disable-next-line ember/no-unused-services
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked toggledLegends: string[] = [];
  @tracked tooltipData: BeniTooltipData | null = null;
  @tracked tooltipElement: HTMLElement | null = null;

  BREAKDOWN_TYPE = BREAKDOWNS.LENGTH_OF_STAY;
  TICK_AMOUNT_DEFAULT = 4;
  TICK_AMOUNT_MIX_VIEW = 6;

  get chartCategories(): string[] {
    const {
      ownComparisonSetMeta,
      primaryComparisonSetMeta,
      secondaryComparisonSetMeta,
    } = this.args;

    const comparisonSetTranslations: string[] = [];

    const pushComparisonSetTranslation = (
      comparisonSetMeta: BeniMetaComparisonSet | null,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ) => {
      if (comparisonSetMeta && comparisonSetMeta.isVisible) {
        const { selectedRange, selectedKpiLosChart } = this.args;

        const isHiddenFutureCompsetData =
          this.benchmarkInsightBeniService.isHiddenFutureCompsetData(
            comparisonSetMeta.comparisonSetType,
            selectedRange.start,
            selectedKpiLosChart,
          );

        if (isHiddenFutureCompsetData) {
          return;
        }

        comparisonSetTranslations.push(
          this.getComparisonSetTypeTranslation(
            comparisonSetMeta.comparisonSetType,
          ),
        );
      }
    };

    [
      ownComparisonSetMeta,
      primaryComparisonSetMeta,
      secondaryComparisonSetMeta,
    ].forEach(pushComparisonSetTranslation);

    return comparisonSetTranslations;
  }

  get chartData(): LosChartSeries[] {
    const {
      ownComparisonSetMeta,
      primaryComparisonSetMeta,
      secondaryComparisonSetMeta,
      ownComparisonSetBreakdowns,
      primaryComparisonSetBreakdowns,
      selectedKpiLosChart,
      selectedLosSortOption,
    } = this.args;

    const ownComparisonSetExists = ownComparisonSetMeta?.isVisible;
    const primaryComparisonSetExists = primaryComparisonSetMeta?.isVisible;
    const secondaryComparisonSetExists = secondaryComparisonSetMeta?.isVisible;

    const breakdownsToSort =
      selectedLosSortOption === DROPDOWN_LOS_SORT_OPTIONS.MY_HIGHEST_PERFORMING
        ? ownComparisonSetBreakdowns
        : primaryComparisonSetBreakdowns;
    const isDifference =
      selectedLosSortOption ===
        DROPDOWN_LOS_SORT_OPTIONS.HIGHEST_POSITIVE_DIFFERENCE_VS_PRIMARY_COMPARISON_SET ||
      selectedLosSortOption ===
        DROPDOWN_LOS_SORT_OPTIONS.HIGHEST_NEGATIVE_DIFFERENCE_VS_PRIMARY_COMPARISON_SET;
    const sortDirection: SortOrderType =
      selectedLosSortOption ===
      DROPDOWN_LOS_SORT_OPTIONS.HIGHEST_NEGATIVE_DIFFERENCE_VS_PRIMARY_COMPARISON_SET
        ? SORT_ORDER.ASCENDING
        : SORT_ORDER.DESCENDING;

    const sortedBreakdowns =
      this.benchmarkInsightBeniChartLosService.sortLosBreakdowns(
        breakdownsToSort,
        selectedKpiLosChart,
        isDifference,
        sortDirection,
      );

    const sortedLosNumbers = sortedBreakdowns
      .map((breakdown) => breakdown.los)
      .filter((n) => !Number.isNaN(n)); // Filter out `total`.

    const topLosNumbers = sortedLosNumbers.slice(0, this.countLos);

    const losSeries: LosChartSeries[] = [];

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const addSeries = (losNumbers: number[]) => {
      losNumbers.forEach((los) => {
        const breakdownsByLos = this.losDict[los] ?? null;

        if (!breakdownsByLos) {
          return;
        }

        const finalSeriesData: LosChartSeriesData = [];

        if (ownComparisonSetExists) {
          finalSeriesData.push(
            ...this.getSeriesData(
              ownComparisonSetMeta.comparisonSetType,
              breakdownsByLos.ownComparisonSetBreakdown,
            ),
          );
        }

        if (primaryComparisonSetExists) {
          finalSeriesData.push(
            ...this.getSeriesData(
              primaryComparisonSetMeta.comparisonSetType,
              breakdownsByLos.primaryComparisonSetBreakdown,
            ),
          );
        }

        if (secondaryComparisonSetExists) {
          finalSeriesData.push(
            ...this.getSeriesData(
              secondaryComparisonSetMeta.comparisonSetType,
              breakdownsByLos.secondaryComparisonSetBreakdown,
            ),
          );
        }

        const series = {
          id: los,
          name: this.intl.t('benchmark_insight.los_chart.legend_label', {
            count: los,
          }),
          data: finalSeriesData,
        };

        losSeries.push(series);
      });
    };

    addSeries(topLosNumbers);

    if (this.args.showMix) {
      const dataSets = losSeries.map((series) => {
        return { data: series.data };
      });
      const orderedData = [...this.sumDataArrays(dataSets)].map((item) => {
        return item.data;
      });

      const seriesOther = {
        id: -1,
        name: this.intl.t('benchmark_insight.los_chart.mix_view.series.other'),
        color: 'var(--prism-color-general-neutral-600)',
        data: orderedData,
        // we can't calculate tooltip data for Others series
        // (we can't simply sum up ADR & RevPAR)
        // so we disable mouse hover and tooltip display for this series
        enableMouseTracking: false,
      };
      losSeries.push(seriesOther);
    }

    return losSeries;
  }

  get chartOptions(): Highcharts.Options {
    const { showMix } = this.args;

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    const stacking = showMix ? 'normal' : undefined;
    const pointPadding = showMix
      ? 0.09
      : BREAKDOWN_CHART_COLUMN_SERIES_POINT_PADDING;
    const groupPadding = showMix
      ? 0
      : BREAKDOWN_CHART_COLUMN_SERIES_GROUP_PADDING;

    return {
      chart: {
        type: 'column',
        zoomType: undefined,
        height: BREAKDOWN_CHART_HEIGHT,
        spacing: [10, 0, 0, 0],
        reflow: true,
        events: {
          load(): void {
            // hacky fix for a bug due to time constraints where
            // the stacked columns collapse on load
            // to be discussed with frontend team and improved BENI-1460
            next(() => {
              // Only reflow if the chart is available, `this.chart` doesn't exist so check on `this.chartHeight` instead.
              if (this.chartHeight) {
                this.reflow();
              }
            });

            this.series.forEach((series) => {
              if (that.toggledLegends.includes(series.name)) {
                series.setVisible(false);
              }
            });
          },
        },
      },
      xAxis: this.xAxis,
      legend: {
        enabled: !this.args.isFiltering,
        itemStyle: {
          color: 'var(--prism-color-text-neutral-subdued)',
        },
        itemHoverStyle: {
          color: 'var(--prism-color-interactive-text-secondary-hover)',
        },
      },
      yAxis: this.yAxis,
      plotOptions: {
        column: {
          stacking,
          pointPadding,
          groupPadding,
          centerInCategory: false,
          pointWidth: undefined,
          minPointLength: 2,
          point: {
            events: {
              // Highlight the hovered data in each series
              mouseOver(): void {
                this.series.chart.series.forEach((series) => {
                  if (series && series.points && series !== this.series) {
                    series.points.forEach((point: ChartCustomPoint) => {
                      if (point.graphic) {
                        point.graphic.element.style.transition = 'opacity 0.3s';
                        point.graphic.attr({
                          opacity: 0.3,
                        });
                      }
                    });
                  }
                }, this);
              },
              mouseOut(): void {
                this.series.chart.series.forEach((series) => {
                  if (series && series.points && series !== this.series) {
                    series.points.forEach((point: ChartCustomPoint) => {
                      if (point.graphic) {
                        point.graphic.attr({
                          opacity: 1,
                        });
                      }
                    });
                  }
                });
              },
            },
          },
        },
        series: {
          cursor: this.args.isFiltering ? 'cursor' : 'pointer',
          animation: true,
          point: {
            events: {
              click(): void {
                const { id: los } = this.series.userOptions;
                if (!los) {
                  return;
                }

                if (that.args.los === los) {
                  return;
                }

                this.series.chart.tooltip.hide();
                that.args.setLos(los);

                that.usageAnalytics.track('Filter Applied', {
                  eventTrigger: 'los',
                  product: 'benchmark-insight',
                  type: 'los',
                  value: los,
                });
              },
            },
          },
          events: {
            legendItemClick(): void {
              if (this.visible) {
                that.toggledLegends.push(this.name);
              } else {
                that.toggledLegends = that.toggledLegends.filter(
                  (legend) => legend !== this.name,
                );
              }
            },
          },
        },
      },
      tooltip: {
        padding: 0,
        borderWidth: 0,
        shadow: false,
        distance: 20,
        outside: true,
        shared: false,
        useHTML: true,
        formatter(): string | null {
          if (!that.tooltipElement) {
            return null;
          }

          // Necessary to get correct data for tooltips, needs to be in separate runloop
          begin();
          const los = this.series.userOptions.id;
          if (!los) {
            return null;
          }

          const losNumber = parseInt(los, 10);

          const combinedBreakdowns = that.losDict[losNumber];
          if (!combinedBreakdowns) {
            return null;
          }

          const tooltipData = {
            ...combinedBreakdowns,
            prefix: that.intl
              .t('benchmark_insight.los_chart.legend_label', {
                count: los,
              })
              .toString(),
          };
          that.tooltipData = tooltipData;
          end();

          return that.tooltipElement.innerHTML;
        },
      },
    };
  }

  get countLos(): number {
    if (this.args.isExpanded) {
      return 10;
    }

    return 5;
  }

  get isChartViewDropdownHidden(): boolean {
    return this.isNotOccupancy || this.args.isFiltering;
  }

  get isNotOccupancy(): boolean {
    return this.args.selectedKpiLosChart !== KPIS.OCCUPANCY;
  }

  get losDict(): BreakdownsDict {
    const {
      ownComparisonSetMeta,
      primaryComparisonSetMeta,
      secondaryComparisonSetMeta,
      ownComparisonSetBreakdowns,
      primaryComparisonSetBreakdowns,
      secondaryComparisonSetBreakdowns,
    } = this.args;

    return buildBreakdownDict({
      ownComparisonSetBreakdowns,
      ownComparisonSetMeta,
      primaryComparisonSetBreakdowns,
      primaryComparisonSetMeta,
      secondaryComparisonSetBreakdowns,
      secondaryComparisonSetMeta,
    });
  }

  get maxXAxisTicIndex(): number {
    const comparisonSetMetas = [
      this.args.ownComparisonSetMeta,
      this.args.primaryComparisonSetMeta,
      this.args.secondaryComparisonSetMeta,
    ];

    return comparisonSetMetas.reduce((count, meta) => {
      const isAvailable = meta?.comparisonSetType && meta.isVisible;
      return isAvailable ? count + 1 : count;
    }, 0);
  }

  get xAxis(): Highcharts.XAxisOptions {
    const { showMix } = this.args;

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    const categories = this.chartCategories;
    const min = showMix ? -1 : undefined;
    const max = showMix ? this.maxXAxisTicIndex : undefined;

    return {
      categories,
      lineWidth: 0,
      tickmarkPlacement: 'on',
      tickWidth: 1,
      minPadding: 0,
      startOnTick: true,

      // Add an extra tick to the left and right of the stacked columns
      // this is the best way to center the columns in the chart
      max,
      min,

      // hide the first and last labels in mix view
      showFirstLabel: !showMix,
      showLastLabel: !showMix,

      // hide the first and last ticks in mix view
      tickPositioner(): Highcharts.AxisTickPositionsArray {
        const categoryIndexies = this.categories.map((x, index) => index);

        if (!that.args.showMix) {
          return categoryIndexies;
        }

        const maxCatIndex = Math.max(...categoryIndexies);
        const maxPlusOne = maxCatIndex + 1;
        const result = [-1, ...categoryIndexies, maxPlusOne];

        let allTicks: Highcharts.Dictionary<Highcharts.Tick> = {};
        if (this.chart.xAxis[0]?.ticks) {
          allTicks = this.chart.xAxis[0]?.ticks;
        }

        const keys = Object.keys(allTicks).map(Number);
        const maxKey = Math.max(...keys);

        const firstTick = this.chart.xAxis[0]?.ticks[-1];
        const lastTick = this.chart.xAxis[0]?.ticks[maxKey];

        firstTick?.mark?.hide();
        lastTick?.mark?.hide();

        return result;
      },
    };
  }

  get yAxis(): Highcharts.YAxisOptions {
    const { showMix } = this.args;

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    const { selectedKpiLosChart } = that.args;
    const max = showMix ? 1 : null;
    const tickAmount = showMix
      ? this.TICK_AMOUNT_MIX_VIEW
      : this.TICK_AMOUNT_DEFAULT;
    return {
      // have the largest blocks at the bottom
      reversedStacks: false,
      max,
      min: 0,
      title: {
        margin: 15,
        text: this.intl.t(KPI_LONG_TRANSLATIONS[selectedKpiLosChart]),
      },
      labels: {
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter() {
          if (typeof this.value !== 'number') {
            return this.value;
          }

          return that.benchmarkInsightBeniChartService.yAxisLabelFormatter(
            this.value,
            selectedKpiLosChart === KPIS.OCCUPANCY,
          );
        },
      },
      tickAmount,
    };
  }

  getComparisonSetTypeTranslation(type: ComparisonSet): string {
    return this.intl.t(BENI_COMPARISON_SET_LONG_TRANSLATIONS[type]);
  }

  getSeriesData(
    comparisonSetType: ComparisonSet,
    breakdown: BeniBreakdown | null,
  ): LosChartSeriesData {
    const { selectedKpiLosChart, showMix } = this.args;

    const result: LosChartSeriesData = [];

    if (!comparisonSetType || !breakdown) {
      return result;
    }

    const { selectedRange } = this.args;

    const isHiddenFutureCompsetData =
      this.benchmarkInsightBeniService.isHiddenFutureCompsetData(
        comparisonSetType,
        selectedRange.start,
        selectedKpiLosChart,
      );

    if (isHiddenFutureCompsetData) {
      return result;
    }

    const correctValue = showMix ? KPIS.OCCUPANCY_MIX : selectedKpiLosChart;

    const value = breakdown.current.total[correctValue];

    result.push(value);

    return result;
  }

  sumDataArrays(
    seriesArray: { data: LosChartSeriesData }[],
  ): { data: number | null; id: number }[] {
    const groupedBySeries =
      this.benchmarkInsightBeniChartLosService.groupDataBySeries(seriesArray);
    const remaindersSeriesData = groupedBySeries.map((group, index) => {
      const remainder = calculateRemainderFromSum(group.values, 1);
      return { id: index, data: remainder };
    });

    return remaindersSeriesData;
  }

  @action
  setTooltipElement(element: HTMLElement): void {
    this.tooltipElement = element;
  }

  <template>
    <div class={{styles.breakdown}}>
      <div class={{styles.breakdown__header}}>
        <div class={{styles.axis-buttons-container}}>
          <div class={{styles.header__axis-buttons}}>
            <HotelBenchmarkInsightButtonGroupChartKpis
              @breakdownType={{this.BREAKDOWN_TYPE}}
              @changeSelectedKpi={{@changeSelectedKpiLosChart}}
              @dataTestId="section-los-kpi-selector"
              @selectedKpi={{@selectedKpiLosChart}}
            />

            <div class={{styles.axis-buttons__los-dropdown}}>
              {{#if @primaryComparisonSet}}
                {{#unless @isFiltering}}
                  <span class={{styles.los-dropdown__label}}>{{t
                      "benchmark_insight.los_chart.show"
                    }}</span>
                  <HotelBenchmarkInsightDropdownsDropdownLosDropdownLosSort
                    @changeSelectedLosSortOption={{@changeSelectedLosSortOption}}
                    @ownComparisonSetMeta={{@ownComparisonSetMeta}}
                    @primaryComparisonSet={{@primaryComparisonSet}}
                    @primaryComparisonSetMeta={{@primaryComparisonSetMeta}}
                    @selectedKpiLosChart={{@selectedKpiLosChart}}
                    @selectedLosSortOption={{@selectedLosSortOption}}
                    @selectedRange={{@selectedRange}}
                  />
                {{/unless}}
              {{/if}}
            </div>

          </div>
        </div>

        <div class={{styles.header__view-buttons}}>
          {{#unless this.isChartViewDropdownHidden}}

            <div class={{styles.view-buttons__line}}></div>

            <div class={{styles.view-buttons__dropdown}}>
              <HotelBenchmarkInsightDropdownsDropdownOccupancyView
                @changeSelectedOccupancyView={{@changeSelectedOccupancyView}}
                @selectedOccupancyView={{@selectedOccupancyView}}
              />
            </div>
          {{/unless}}
        </div>

      </div>

      {{#if @showSkeleton}}
        <div class={{styles.breakdown__skeleton}}>
          <HotelBenchmarkInsightSkeleton
            @imageHtml={{SkeletonBreakdownChartIllustration}}
            @showAnimation={{@showSkeletonAnimation}}
          />
        </div>
      {{else}}
        <GeneralChartsOtaChart
          @chartOptions={{this.chartOptions}}
          @model={{this.chartData}}
          @onUpdateChartReference={{@onUpdateChartReference}}
        />
      {{/if}}
    </div>

    <div hidden {{getElement this.setTooltipElement}}>
      {{#if this.tooltipData}}
        <HotelBenchmarkInsightTooltipsBeniTooltip
          @ownComparisonSetMeta={{@ownComparisonSetMeta}}
          @pickupTranslation={{@pickupTranslation}}
          @primaryComparisonSetMeta={{@primaryComparisonSetMeta}}
          @referencePeriod={{@referencePeriod}}
          @secondaryComparisonSetMeta={{@secondaryComparisonSetMeta}}
          @selectedKpi={{@selectedKpiLosChart}}
          @selectedRange={{@selectedRange}}
          @showMix={{@showMix}}
          @tooltipData={{this.tooltipData}}
          @varianceTranslation={{@varianceTranslation}}
          @visibleKpis={{@visibleKpis}}
        />
      {{/if}}
    </div>
  </template>
}
